// Pure utilities for laying out a flat question list as the prototype's
// rendered tree: split by target, sort by `order`, assign display numbers
// (`N` for top-level, `N-M` for sub-questions). React components consume the
// output and never call this with a different shape — keeping the logic
// pure so it can be unit-tested without a renderer.

import type { Question, Target } from '../types/questionnaire-design.types';

export interface QuestionRenderNode {
  question: Question;
  // '1', '2' for top-level; '1-1', '1-2' for sub.
  displayNum: string;
  children: QuestionRenderNode[];
}

function byOrder(a: Question, b: Question): number {
  return a.order - b.order;
}

export function groupQuestionsForRender(
  questions: Question[],
  target: Target,
): QuestionRenderNode[] {
  const inTarget = questions.filter((q) => q.target === target);
  const topLevel = inTarget.filter((q) => q.parentQid === null).sort(byOrder);

  return topLevel.map((parent, i) => {
    const subs = inTarget
      .filter((q) => q.parentQid === parent.qid)
      .sort(byOrder);
    return {
      question: parent,
      displayNum: String(i + 1),
      children: subs.map((sub, j) => ({
        question: sub,
        displayNum: `${i + 1}-${j + 1}`,
        children: [],
      })),
    };
  });
}

// Locate the display number for a given QID across both target buckets. Used
// when rendering branch chips so a JUMP target shows the same number the
// user sees on the destination card.
export function findDisplayNum(
  questions: Question[],
  qid: string,
): string | null {
  for (const target of ['PATIENT', 'CAREGIVER'] as const) {
    const tree = groupQuestionsForRender(questions, target);
    for (const top of tree) {
      if (top.question.qid === qid) return top.displayNum;
      for (const sub of top.children) {
        if (sub.question.qid === qid) return sub.displayNum;
      }
    }
  }
  return null;
}
